import React, { useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import styles from "./DraggableItem.css";
import DragHandle from "./DragHandle";
import { ItemTypes } from "./ItemTypes";

const DraggableItem = ({
	id,
	row,
	col,
	item,
	moveComponent,
	insertNewComponent,
	getParentBoundingBox,
	getNumberOfElementsInRow,
	onDrop,
	isPlaceholder,
	pageIsEmpty,
	children
}) => {
	const ref = useRef(null);
	const [ showPlaceholder, setShowPlaceholder ] = useState(true);

	const getValidDropAcceptance = () => {
		if (isPlaceholder) {
			// any component can drop on a placeholder
			return [ ItemTypes.SINGLE_COLUMN_COMPONENT, ItemTypes.COMPONENT ];
		} else if (item.itemType === ItemTypes.SINGLE_COLUMN_COMPONENT) {
			// no other component and drop on a single column component
			return "";
		} else {
			return ItemTypes.COMPONENT;
		}
	};

	const [ , drop ] = useDrop({
		accept: getValidDropAcceptance(),
		hover (item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragRow = item.row;
			const dragCol = item.col;
			const hoverRow = row;
			const hoverCol = col;
			// Don't replace items with themselves
			if (dragRow === hoverRow && dragCol === hoverCol) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			const parentBoundingRect = getParentBoundingBox();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;


			if (dragRow !== -1 && dragCol !== -1) {
				// Only perform the move when the mouse has crossed half of the items height
				// When dragging downwards, only move when the cursor is below 50%
				// When dragging upwards, only move when the cursor is above 50%
				// Dragging downwards
				if (dragRow < hoverRow && hoverClientY < hoverMiddleY) {
					return;
				}
				// Dragging upwards
				if (dragRow > hoverRow && hoverClientY > hoverMiddleY) {
					return;
				}
			}
			const hoverPositionX = clientOffset.x - parentBoundingRect.x;
			const boxWidth = parentBoundingRect.width;
			const numberOfElements = getNumberOfElementsInRow(hoverRow);

			if (numberOfElements >= 3 && dragRow !== hoverRow) {
				return; // we have max 3 elements in a row
			}

			let newBoxPosition = 0;
			if (numberOfElements > 0) {
				newBoxPosition = Math.max(Math.floor(hoverPositionX / (boxWidth / numberOfElements)), 0);
			}

			//  <--- box width   --->
			//  <- el w  ->           <= elements = 2
			// |          0         | <= number of elements = 1
			// |     0    |     1   | <= number of elements = 2
			// |  0  |   1  |   2   |
			//         x <----          hover position

			if (newBoxPosition === dragCol && dragRow === hoverRow) {
				return;
			}

			// Time to actually perform the action
			if (dragRow === -1 && dragCol === -1) {
				insertNewComponent(item.item, hoverRow, newBoxPosition);
			} else {
				moveComponent(dragRow, dragCol, hoverRow, newBoxPosition);
			}

			if (item.latestPlaceholder !== setShowPlaceholder && item.latestPlaceholder !== null) {
				item.latestPlaceholder(true);
				item.latestPlaceholder = null;
			}
			if (isPlaceholder) {
				setShowPlaceholder(false);
				item.latestPlaceholder = setShowPlaceholder;
			}

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.row = hoverRow;
			item.col = newBoxPosition;
		},
	});
	drop(ref);
	const [ { isDragging }, drag, preview ] = useDrag({
		item: {
			type: item.itemType || ItemTypes.COMPONENT,
			id,
			row,
			col,
			item,
			latestPlaceholder: null
		},
		collect: monitor => {
			return ({
				isDragging: monitor.isDragging()
			});
		},
		isDragging (monitor) {
			return id === monitor.getItem().id;
		},
		end (item, monitor) {
			onDrop();
		}
	});
	const opacity = isDragging ? 0 : 1;
	const display = showPlaceholder ? "" : "none";
	let className = isDragging ? styles.paddingDragging : styles.paddingNormal;
	if (pageIsEmpty) {
		className = styles.paddingEmptyPage;
	} else if (item.type === "") {
		className = styles.paddingPlaceholder;
	}
	useEffect(
		() => {
			preview(getEmptyImage(), { captureDraggingState: true });
		},
		[
			preview
		]
	);

	return (
		<div ref={ref} style={{ display }} className={className}>
			<div style={{ opacity }}>
				{item.type !== "" ? (
					<div className="ui segment">
						<div className={styles.itemBox}>
							<div style={{ flexGrow: 24 }}>
								{children}
							</div>
							<div ref={drag}>
								<DragHandle></DragHandle>
							</div>
						</div>
					</div>
				) : (
					""
				)}
			</div>
		</div>
	);
};

export default DraggableItem;
